import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

/**
 * Tracks a learning session for the currently signed-in student.
 * - Creates a `learning_sessions` row on first activity.
 * - Notifies linked parents (email + in-app) on session start.
 * - Heartbeats every 60s while the tab is visible.
 * - Closes the session via edge function after 10 min of inactivity,
 *   on tab hide > 5 min, or on `beforeunload` (best effort).
 * - Exposes a window event `kelasai:activity` for other code to log activities.
 */

type Activity = {
  activity_type: "ai_message" | "exercise" | "quiz" | "read" | "notion_view";
  subject?: string | null;
  notion?: string | null;
  score?: number | null;
  total?: number | null;
  metadata?: Record<string, any>;
};

declare global {
  interface WindowEventMap {
    "kelasai:activity": CustomEvent<Activity>;
  }
}

const INACTIVITY_MS = 10 * 60 * 1000; // 10 min
const HIDDEN_MS = 5 * 60 * 1000; // 5 min
const HEARTBEAT_MS = 60 * 1000;

function detectDeviceType(): string {
  if (typeof navigator === "undefined") return "Inconnu";
  const ua = navigator.userAgent;
  if (/Mobi|Android|iPhone/i.test(ua)) return "Mobile";
  if (/iPad|Tablet/i.test(ua)) return "Tablette";
  return "Ordinateur";
}

export function useLearningSession() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const sessionIdRef = useRef<string | null>(null);
  const isStudentRef = useRef<boolean>(false);
  const lastActivityRef = useRef<number>(Date.now());
  const hiddenSinceRef = useRef<number | null>(null);
  const closingRef = useRef<boolean>(false);

  // Check role once per user
  useEffect(() => {
    if (!user) {
      isStudentRef.current = false;
      return;
    }
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", user.id)
        .maybeSingle();
      isStudentRef.current = data?.role === "eleve" || data?.role === "etudiant";
    })();
  }, [user?.id]);

  // Track route changes as a heartbeat
  useEffect(() => {
    lastActivityRef.current = Date.now();
  }, [location.pathname]);

  const startSession = async () => {
    if (!user || sessionIdRef.current || !isStudentRef.current) return;
    const device = detectDeviceType();
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : null;
    const { data, error } = await (supabase as any)
      .from("learning_sessions")
      .insert({
        student_id: user.id,
        device_type: device,
        device_user_agent: ua,
        status: "active",
      })
      .select("id")
      .single();
    if (error || !data) {
      console.warn("learning session start failed", error);
      return;
    }
    sessionIdRef.current = data.id;
    try {
      await supabase.functions.invoke("notify-parent-login", {
        body: { session_id: data.id, device_type: device },
      });
    } catch (e) {
      console.warn("notify-parent-login failed", e);
    }
  };

  const closeSession = async () => {
    const id = sessionIdRef.current;
    if (!id || closingRef.current) return;
    closingRef.current = true;
    sessionIdRef.current = null;
    try {
      await supabase.functions.invoke("close-learning-session", {
        body: { session_id: id },
      });
    } catch (e) {
      console.warn("close-learning-session failed", e);
    } finally {
      closingRef.current = false;
    }
  };

  const heartbeat = async () => {
    const id = sessionIdRef.current;
    if (!id) return;
    await (supabase as any)
      .from("learning_sessions")
      .update({ last_seen_at: new Date().toISOString() })
      .eq("id", id);
  };

  // Listen to activity events
  useEffect(() => {
    if (!user || loading) return;
    const handler = async (ev: CustomEvent<Activity>) => {
      if (!isStudentRef.current) return;
      lastActivityRef.current = Date.now();
      if (!sessionIdRef.current) await startSession();
      const sid = sessionIdRef.current;
      if (!sid) return;
      const a = ev.detail || ({} as Activity);
      await (supabase as any).from("session_activities").insert({
        session_id: sid,
        student_id: user.id,
        activity_type: a.activity_type,
        subject: a.subject ?? null,
        notion: a.notion ?? null,
        score: a.score ?? null,
        total: a.total ?? null,
        metadata: a.metadata ?? {},
      });
    };
    window.addEventListener("kelasai:activity", handler as EventListener);
    return () => window.removeEventListener("kelasai:activity", handler as EventListener);
  }, [user?.id, loading]);

  // Visibility, inactivity, beforeunload
  useEffect(() => {
    if (!user || loading) return;

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenSinceRef.current = Date.now();
      } else {
        hiddenSinceRef.current = null;
        lastActivityRef.current = Date.now();
      }
    };
    const onUserAction = () => {
      lastActivityRef.current = Date.now();
    };
    const onUnload = () => {
      const id = sessionIdRef.current;
      if (!id) return;
      // Best-effort: fire and forget
      try {
        const url = `https://pywlztwjzurvozenifji.supabase.co/functions/v1/close-learning-session`;
        const blob = new Blob(
          [JSON.stringify({ session_id: id })],
          { type: "application/json" }
        );
        navigator.sendBeacon?.(url, blob);
      } catch {}
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("click", onUserAction);
    window.addEventListener("keydown", onUserAction);
    window.addEventListener("beforeunload", onUnload);

    const interval = window.setInterval(async () => {
      if (!sessionIdRef.current) return;
      const now = Date.now();
      const inactive = now - lastActivityRef.current > INACTIVITY_MS;
      const hiddenTooLong = hiddenSinceRef.current && now - hiddenSinceRef.current > HIDDEN_MS;
      if (inactive || hiddenTooLong) {
        await closeSession();
      } else if (document.visibilityState === "visible") {
        await heartbeat();
      }
    }, HEARTBEAT_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("click", onUserAction);
      window.removeEventListener("keydown", onUserAction);
      window.removeEventListener("beforeunload", onUnload);
      window.clearInterval(interval);
    };
  }, [user?.id, loading]);
}

export function trackLearningActivity(detail: Activity) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("kelasai:activity", { detail }));
}